import { IPagination } from 'src/app/shared/models/page.model';
import { IAgent, IAgentFilter } from '../models/agents.models';
import { Action, createReducer, on } from '@ngrx/store';
import * as agentsActions from './agents.actions';
import { ICatalog } from '../../catalogs/models/catalogs.models';
import { ICommission } from '../models/commissions.models';
import { refillAgentFilter } from 'src/app/shared/utils/helpers';

export interface AgentsFilterState {
  advanced: IAgentFilter;
  keyword: string;
}

export interface AgentsState {
  metadata: IPagination | null;
  agents: IAgent[];
  catalogs: ICatalog[];
  commissions: ICommission[];
  agent: IAgent | null;
  commission: ICommission | null;
  loading: boolean;
  agentFilterState: AgentsFilterState;
}

export const initialAgentsState: AgentsState = {
  metadata: null,
  agents: [],
  commissions: [],
  catalogs: [],
  agent: null,
  commission: null,
  loading: false,
  agentFilterState: {
    advanced: refillAgentFilter(),
    keyword: ''
  },
};

export interface IFilterState {
  advanced: IAgentFilter;
  keyword: string;
}

const featureReducer = createReducer(
  initialAgentsState,
  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                      Agent Reducers                                ║
   * ║                                                                    ║
   * ║   All reducers related to commission management in the application.║
   * ║   Includes adding, updating, and deleting agents.                  ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */
  on(agentsActions.GetAgentsSuccess, (state, action) => ({
    ...state,
    agents: action.agents.data,
    metadata: action.agents.metadata
  })),
  on(agentsActions.GetAgentByIdSuccess, (state, action) => ({
    ...state,
    agent: action.agent,
  })),
  on(agentsActions.AddAgentSuccess, (state, action) => ({
    ...state,
    agents: [action.agent as IAgent, ...state.agents],
  })),
  on(agentsActions.UpdateAgentSuccess, (state, action) => ({
    ...state,
    agents: state.agents.map((agent) =>
      agent.id === action.agent.id ? action.agent : agent
    ),
    agent: action.agent,
  })),
  on(agentsActions.DeleteAgentsSuccess, (state, action) => ({
    ...state,
    agents: [
      ...state.agents.filter(
        (agent) =>
          !action.ids.some((deletedAgentId) => deletedAgentId === agent.id)
      ),
    ],
  })),
  on(agentsActions.GetAgentsCatalogsSuccess, (state, action) => ({
    ...state,
    catalogs: action.catalogs.data,
  })),
  on(agentsActions.ClearAgent, (state) => ({
    ...state,
    agent: null,
  })),
  on(agentsActions.SetAgentFilter, (state, action) => ({
    ...state,
    agentFilterState: {
      advanced: action.advanced!,
      keyword : action.keyword
    }
  })),
  on(agentsActions.SetAgentSearch, (state, action) => ({
    ...state,
    agentFilterState: {
      ...state.agentFilterState,
      keyword: action.keyword,
    }
  })),
  /**
   * ╔════════════════════════════════════════════════════════════════════╗
   * ║                                                                    ║
   * ║                      Commission Reducers                           ║
   * ║                                                                    ║
   * ║   All reducers related to commission management in the application. ║
   * ║   Includes loading, adding, updating, and deleting commissions.     ║
   * ║                                                                    ║
   * ╚════════════════════════════════════════════════════════════════════╝
   */
  on(agentsActions.GetCommissionsSuccess, (state, action) => ({
    ...state,
    commissions: action.commissions.data,
    commissionsMetadata: action.commissions.metadata
  })),
  on(agentsActions.GetCommissionByIdSuccess, (state, action) => ({
    ...state,
    commission: action.commission,
  })),
  on(agentsActions.AddCommissionSuccess, (state, action) => ({
    ...state,
    commissions: [action.commission, ...(state.commissions || [])],
  })),
  on(agentsActions.UpdateCommissionSuccess, (state, action) => ({
    ...state,
    commissions: state.commissions.map((commission) =>
      commission.id === action.commission.id ? action.commission : commission
    ),
    commission: action.commission,
  })),
  on(agentsActions.DeleteCommissionsSuccess, (state, action) => ({
    ...state,
    commissions: [
      ...state.commissions.filter(
        (commission) =>
          !action.ids.some((deletedCommissionId) => deletedCommissionId === commission.id)
      ),
    ],
  })),
  on(agentsActions.ClearCommission, (state) => ({
    ...state,
    commission: null,
  })),
);

export function agentsReducer(state: AgentsState | undefined, action: Action) {
  return featureReducer(state, action);
}
